
import { useState } from "react";
import { Helmet } from "react-helmet";
import { Slider } from "@/components/ui/slider";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart, TrendingUp, Clock, DollarSign, PieChart, AreaChart, Save, Share2, FileDown, RotateCw } from "lucide-react";
import { motion } from "framer-motion";
import { useToast } from "@/components/ui/use-toast";

const ROICalculator = () => {
  const { toast } = useToast();
  const [employees, setEmployees] = useState([100]);
  const [hoursPerWeek, setHoursPerWeek] = useState([10]);
  const [dailyRate, setdailyRate] = useState([75]);
  const [activeTab, setActiveTab] = useState("roi"); // roi, hours, time
  const [savedCalculations, setSavedCalculations] = useState<any[]>([]);
  
  // Calculate savings
  const weeklySavingsHours = Math.round((employees[0] * hoursPerWeek[0]) * 0.3); // 30% time savings
  const annualSavings = Math.round(weeklySavingsHours * 52 * dailyRate[0]);
  const implementationTime = Math.round(3 + (employees[0] / 100)); // Base 3 months + 1 month per 100 employees
  const roi = Math.round((annualSavings / (implementationTime * 0.5 * employees[0] * dailyRate[0])) * 100);
  
  // Format with commas
  const formatNumber = (num: number) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const saveCalculation = () => {
    const newCalculation = {
      id: Date.now(),
      employees: employees[0],
      hoursPerWeek: hoursPerWeek[0],
      dailyRate: dailyRate[0],
      weeklySavingsHours,
      annualSavings,
      implementationTime,
      roi,
      date: new Date()
    };
    
    setSavedCalculations([...savedCalculations, newCalculation]);
    
    toast({
      title: "Calculation Saved",
      description: "Your ROI calculation has been saved.",
      variant: "success"
    });
  };

  const shareCalculation = () => {
    // Generate shareable link or open share dialog
    const shareData = {
      title: 'DGK Hub ROI Calculator',
      text: `Check out my ROI calculation with DGK Hub: ${roi}% ROI, $${formatNumber(annualSavings)} annual savings`,
      url: window.location.href
    };
    
    if (navigator.share) {
      navigator.share(shareData)
        .then(() => {
          toast({
            title: "Shared Successfully",
            description: "Your ROI calculation has been shared.",
          });
        })
        .catch((error) => {
          toast({
            title: "Share Failed",
            description: "Could not share the calculation.",
            variant: "destructive"
          });
          console.error('Error sharing:', error);
        });
    } else {
      // Fallback for browsers that don't support the Web Share API
      navigator.clipboard.writeText(shareData.text + " " + shareData.url);
      toast({
        title: "Link Copied",
        description: "Share link copied to clipboard.",
      });
    }
  };

  const exportCalculation = () => {
    const calculationData = {
      title: "DGK Hub ROI Calculation",
      date: new Date().toLocaleDateString(),
      inputs: {
        employees: employees[0],
        hoursPerWeek: hoursPerWeek[0],
        dailyRate: dailyRate[0],
      },
      results: {
        weeklySavingsHours,
        annualSavings,
        implementationTime,
        roi,
      },
    };
    
    const jsonString = JSON.stringify(calculationData, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement("a");
    link.href = url;
    link.download = `roi-calculation-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Export Complete",
      description: "Your ROI calculation has been exported.",
    });
  };

  const resetCalculator = () => {
    setEmployees([100]);
    setHoursPerWeek([10]);
    setdailyRate([75]);
    setActiveTab("roi");
    
    toast({
      title: "Calculator Reset",
      description: "All values have been reset to defaults.",
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { type: "spring", stiffness: 100 }
    }
  };

  const sliderVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { type: "spring", stiffness: 100 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring", 
        stiffness: 60,
        delay: 0.4
      }
    }
  };

  const calculateProgress = (value: number, min: number, max: number) => {
    return ((value - min) / (max - min)) * 100;
  };

  const renderActiveTab = () => {
    switch(activeTab) {
      case "roi":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-md border border-emerald-50">
              <div className="flex items-center mb-4">
                <div className="p-3 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 text-white mr-4">
                  <DollarSign className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Annual Savings</div>
                  <div className="text-3xl font-bold text-emerald-600">${formatNumber(annualSavings)}</div>
                </div>
              </div>
              <div className="space-y-2 mt-4">
                <div className="text-sm text-gray-600 flex justify-between">
                  <span>Average Industry Savings</span>
                  <span className="font-semibold text-gray-800">${formatNumber(Math.round(annualSavings * 0.7))}</span>
                </div>
                <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gray-400 rounded-full" style={{width: '70%'}}></div>
                </div>
                
                <div className="text-sm text-gray-600 flex justify-between mt-2">
                  <span>Your Potential Savings</span>
                  <span className="font-semibold text-emerald-600">${formatNumber(annualSavings)}</span>
                </div>
                <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 rounded-full" style={{width: '100%'}}></div>
                </div>
              </div>
            </div>
            
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-md border border-sky-50">
              <div className="flex items-center mb-4">
                <div className="p-3 rounded-full bg-gradient-to-br from-sky-400 to-sky-600 text-white mr-4">
                  <TrendingUp className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Return on Investment</div>
                  <div className="text-3xl font-bold text-sky-600">{roi}%</div>
                </div>
              </div>
              
              <div className="space-y-2 mt-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Implementation Cost</span>
                  <span className="font-semibold text-gray-800">
                    ${formatNumber(Math.round(implementationTime * 0.5 * employees[0] * dailyRate[0]))}
                  </span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Break-even Point</span>
                  <span className="font-semibold text-gray-800">
                    {Math.round((implementationTime * 0.5 * employees[0] * dailyRate[0]) / (annualSavings / 12))} months
                  </span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">5-Year Total ROI</span>
                  <span className="font-semibold text-sky-600">
                    ${formatNumber(Math.round(annualSavings * 5 - (implementationTime * 0.5 * employees[0] * dailyRate[0])))}
                  </span>
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="text-xs text-gray-500 mb-1">ROI Timeline</div>
                  <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-amber-400 via-sky-500 to-emerald-500 rounded-full animate-pulse" style={{width: `${Math.min(100, roi)}%`}}></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Investment</span>
                    <span>Break-even</span>
                    <span>Profit</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      
      case "hours":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-md border border-cyan-50">
              <div className="flex items-center mb-4">
                <div className="p-3 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 text-white mr-4">
                  <Clock className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Hours Saved Weekly</div>
                  <div className="text-3xl font-bold text-cyan-600">{formatNumber(weeklySavingsHours)} hrs</div>
                </div>
              </div>
              
              <div className="mt-4 space-y-4">
                <div className="bg-white rounded-lg p-3 border border-gray-100">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Per Employee</span>
                    <span className="font-semibold text-gray-800">{(weeklySavingsHours / employees[0]).toFixed(1)} hrs/week</span>
                  </div>
                  <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-cyan-300 to-cyan-500 rounded-full" 
                         style={{width: `${(weeklySavingsHours / employees[0]) / hoursPerWeek[0] * 100}%`}}></div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-3 border border-gray-100">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Monthly Team Hours</span>
                    <span className="font-semibold text-gray-800">{formatNumber(weeklySavingsHours * 4)} hrs</span>
                  </div>
                  <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-cyan-400 to-cyan-600 rounded-full" style={{width: '100%'}}></div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-3 border border-gray-100">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Annual Team Hours</span>
                    <span className="font-semibold text-cyan-600">{formatNumber(weeklySavingsHours * 52)} hrs</span>
                  </div>
                  <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-cyan-500 to-cyan-700 rounded-full" style={{width: '100%'}}></div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-md border border-teal-50">
              <div className="flex items-center mb-4">
                <div className="p-3 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 text-white mr-4">
                  <AreaChart className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Productivity Impact</div>
                  <div className="text-3xl font-bold text-teal-600">+30%</div>
                </div>
              </div>
              
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Project Delivery Speed</span>
                  <div className="flex items-center">
                    <span className="text-emerald-500 mr-2">↑</span>
                    <span className="font-semibold">25%</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Error Reduction</span>
                  <div className="flex items-center">
                    <span className="text-emerald-500 mr-2">↑</span>
                    <span className="font-semibold">40%</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Staff Satisfaction</span>
                  <div className="flex items-center">
                    <span className="text-emerald-500 mr-2">↑</span>
                    <span className="font-semibold">27%</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Client Satisfaction</span>
                  <div className="flex items-center">
                    <span className="text-emerald-500 mr-2">↑</span>
                    <span className="font-semibold">35%</span>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="text-sm text-gray-600 mb-2">Equivalent Full-Time Employees</div>
                  <div className="flex items-center">
                    <div className="text-2xl font-bold text-teal-600 mr-2">
                      {Math.round((weeklySavingsHours / 40) * 10) / 10}
                    </div>
                    <span className="text-sm text-gray-500">FTE</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Your time savings equate to adding {Math.round((weeklySavingsHours / 40) * 10) / 10} full-time employees to your team
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      
      case "time":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-md border border-amber-50">
              <div className="flex items-center mb-4">
                <div className="p-3 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-white mr-4">
                  <BarChart className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Implementation Time</div>
                  <div className="text-3xl font-bold text-amber-600">{implementationTime} months</div>
                </div>
              </div>
              
              <div className="mt-6 space-y-4">
                <div className="relative pt-1">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-xs font-semibold inline-block text-gray-600">
                        Planning & Analysis
                      </span>
                    </div>
                    <div>
                      <span className="text-xs font-semibold inline-block text-amber-600">
                        1 month
                      </span>
                    </div>
                  </div>
                  <div className="flex h-2 overflow-hidden rounded-full bg-gray-100">
                    <div style={{ width: `${100 / implementationTime}%` }} className="bg-amber-400"></div>
                  </div>
                </div>
                
                <div className="relative pt-1">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-xs font-semibold inline-block text-gray-600">
                        System Setup & Configuration
                      </span>
                    </div>
                    <div>
                      <span className="text-xs font-semibold inline-block text-amber-600">
                        {Math.max(1, Math.round(implementationTime * 0.3))} months
                      </span>
                    </div>
                  </div>
                  <div className="flex h-2 overflow-hidden rounded-full bg-gray-100">
                    <div style={{ width: `${(Math.max(1, Math.round(implementationTime * 0.3)) / implementationTime) * 100}%` }} className="bg-amber-500"></div>
                  </div>
                </div>
                
                <div className="relative pt-1">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-xs font-semibold inline-block text-gray-600">
                        Data Migration & Integration
                      </span>
                    </div>
                    <div>
                      <span className="text-xs font-semibold inline-block text-amber-600">
                        {Math.max(1, Math.round(implementationTime * 0.4))} months
                      </span>
                    </div>
                  </div>
                  <div className="flex h-2 overflow-hidden rounded-full bg-gray-100">
                    <div style={{ width: `${(Math.max(1, Math.round(implementationTime * 0.4)) / implementationTime) * 100}%` }} className="bg-amber-600"></div>
                  </div>
                </div>
                
                <div className="relative pt-1">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-xs font-semibold inline-block text-gray-600">
                        Training & Rollout
                      </span>
                    </div>
                    <div>
                      <span className="text-xs font-semibold inline-block text-amber-600">
                        {Math.max(1, Math.round(implementationTime * 0.3))} months
                      </span>
                    </div>
                  </div>
                  <div className="flex h-2 overflow-hidden rounded-full bg-gray-100">
                    <div style={{ width: `${(Math.max(1, Math.round(implementationTime * 0.3)) / implementationTime) * 100}%` }} className="bg-gradient-to-r from-amber-500 to-emerald-500"></div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 shadow-md border border-blue-50">
              <div className="flex items-center mb-4">
                <div className="p-3 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 text-white mr-4">
                  <PieChart className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-sm text-gray-500 mb-1">Resource Requirements</div>
                  <div className="text-3xl font-bold text-blue-600">Optimized</div>
                </div>
              </div>
              
              <div className="mt-4 space-y-4">
                <div className="bg-white rounded-lg p-4 border border-gray-100">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">IT Resources</span>
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <div className="w-3 h-3 rounded-full bg-gray-200"></div>
                      <div className="w-3 h-3 rounded-full bg-gray-200"></div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">1 IT specialist per 200 employees</p>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-gray-100">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">Training Hours</span>
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <div className="w-3 h-3 rounded-full bg-gray-200"></div>
                      <div className="w-3 h-3 rounded-full bg-gray-200"></div>
                      <div className="w-3 h-3 rounded-full bg-gray-200"></div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">4 hours per employee</p>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-gray-100">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">Management Involvement</span>
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <div className="w-3 h-3 rounded-full bg-gray-200"></div>
                      <div className="w-3 h-3 rounded-full bg-gray-200"></div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">10 hours per week during implementation</p>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-gray-100">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-600">Support Requirements</span>
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <div className="w-3 h-3 rounded-full bg-gray-200"></div>
                      <div className="w-3 h-3 rounded-full bg-gray-200"></div>
                      <div className="w-3 h-3 rounded-full bg-gray-200"></div>
                      <div className="w-3 h-3 rounded-full bg-gray-200"></div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Minimal post-implementation support</p>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50/40 to-emerald-50/30 py-10">
      <Helmet>
        <title>ROI Calculator | DGK Hub</title>
        <meta name="description" content="Calculate the return on investment for implementing DGK Hub in your organization" />
      </Helmet>
      
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">ROI Calculator</h1>
            <p className="text-gray-600 mt-1">Calculate the potential return on investment for your organization</p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={resetCalculator} className="flex items-center gap-1">
              <RotateCw className="w-4 h-4" />
              Reset
            </Button>
            <Button size="sm" variant="outline" onClick={saveCalculation} className="flex items-center gap-1">
              <Save className="w-4 h-4" />
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={shareCalculation} className="flex items-center gap-1">
              <Share2 className="w-4 h-4" />
              Share
            </Button>
            <Button size="sm" variant="outline" onClick={exportCalculation} className="flex items-center gap-1">
              <FileDown className="w-4 h-4" />
              Export
            </Button>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="w-full md:w-1/3">
              <div className="space-y-8">
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-gray-700 font-medium">Number of Employees</label>
                    <Badge variant="outline" className="text-emerald-600 bg-emerald-50 border-emerald-200">
                      {employees[0]}
                    </Badge>
                  </div>
                  <div className="relative mt-1 mb-6">
                    <Slider 
                      defaultValue={[100]} 
                      max={1000} 
                      min={50} 
                      step={50} 
                      value={employees}
                      onValueChange={setEmployees} 
                      className="py-4"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>50</span>
                      <span>1,000+</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-gray-700 font-medium">Hours Spent on Manual Tasks (per employee/week)</label>
                    <Badge variant="outline" className="text-emerald-600 bg-emerald-50 border-emerald-200">
                      {hoursPerWeek[0]} hrs
                    </Badge>
                  </div>
                  <div className="relative mt-1 mb-6">
                    <Slider 
                      defaultValue={[10]} 
                      max={40} 
                      min={1} 
                      step={1} 
                      value={hoursPerWeek}
                      onValueChange={setHoursPerWeek} 
                      className="py-4"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>1 hr</span>
                      <span>40 hrs</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-gray-700 font-medium">Average Daily Rate ($)</label>
                    <Badge variant="outline" className="text-emerald-600 bg-emerald-50 border-emerald-200">
                      ${dailyRate[0]}
                    </Badge>
                  </div>
                  <div className="relative mt-1 mb-6">
                    <Slider 
                      defaultValue={[75]} 
                      max={200} 
                      min={25} 
                      step={5} 
                      value={dailyRate}
                      onValueChange={setdailyRate} 
                      className="py-4"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>$25</span>
                      <span>$200</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-emerald-50 to-sky-50 rounded-xl p-5 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">ROI Summary</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                      <div className="flex items-center">
                        <div className="p-2 rounded-lg bg-emerald-100 mr-3">
                          <DollarSign className="w-4 h-4 text-emerald-600" />
                        </div>
                        <span className="text-sm text-gray-700">Annual Savings</span>
                      </div>
                      <span className="font-bold text-emerald-600">${formatNumber(annualSavings)}</span>
                    </div>
                    
                    <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                      <div className="flex items-center">
                        <div className="p-2 rounded-lg bg-sky-100 mr-3">
                          <Clock className="w-4 h-4 text-sky-600" />
                        </div>
                        <span className="text-sm text-gray-700">Weekly Hours Saved</span>
                      </div>
                      <span className="font-bold text-sky-600">{formatNumber(weeklySavingsHours)}</span>
                    </div>
                    
                    <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                      <div className="flex items-center">
                        <div className="p-2 rounded-lg bg-purple-100 mr-3">
                          <TrendingUp className="w-4 h-4 text-sky-600" />
                        </div>
                        <span className="text-sm text-gray-700">ROI Percentage</span>
                      </div>
                      <span className="font-bold text-sky-600">{roi}%</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="p-2 rounded-lg bg-amber-100 mr-3">
                          <BarChart className="w-4 h-4 text-amber-600" />
                        </div>
                        <span className="text-sm text-gray-700">Implementation Time</span>
                      </div>
                      <span className="font-bold text-amber-600">{implementationTime} months</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="w-full md:w-2/3">
              <Card className="bg-gradient-to-br from-white to-gray-50 border-none shadow-xl overflow-hidden h-full">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-sky-500 to-emerald-500" />
                <CardContent className="p-6">
                  <div className="mb-6">
                    <div className="flex items-center justify-center gap-4 mb-6">
                      <button
                        onClick={() => setActiveTab("roi")}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                          activeTab === "roi" 
                            ? "bg-gradient-to-r from-sky-500 to-sky-600 text-white shadow-md" 
                            : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                        }`}
                      >
                        <div className="flex items-center justify-center">
                          <TrendingUp className="w-4 h-4 mr-1" />
                          <span>ROI</span>
                        </div>
                      </button>
                      <button
                        onClick={() => setActiveTab("hours")}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                          activeTab === "hours" 
                            ? "bg-gradient-to-r from-cyan-500 to-cyan-600 text-white shadow-md" 
                            : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                        }`}
                      >
                        <div className="flex items-center justify-center">
                          <Clock className="w-4 h-4 mr-1" />
                          <span>Time Savings</span>
                        </div>
                      </button>
                      <button
                        onClick={() => setActiveTab("time")}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                          activeTab === "time" 
                            ? "bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-md" 
                            : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                        }`}
                      >
                        <div className="flex items-center justify-center">
                          <BarChart className="w-4 h-4 mr-1" />
                          <span>Implementation</span>
                        </div>
                      </button>
                    </div>
                  </div>
                  
                  {renderActiveTab()}
                  
                  <div className="mt-8 text-sm text-gray-500 italic">
                    * Calculations are estimates based on industry averages. Actual results may vary.
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
        
        {savedCalculations.length > 0 && (
          <div className="mt-10">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Saved Calculations</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {savedCalculations.map((calculation) => (
                <Card key={calculation.id} className="border-gray-200 hover:border-emerald-200 transition-colors">
                  <CardContent className="p-5">
                    <div className="flex justify-between items-start mb-4">
                      <div className="text-lg font-semibold text-gray-800">ROI: {calculation.roi}%</div>
                      <Badge variant="outline" className="text-xs">
                        {new Date(calculation.date).toLocaleDateString()}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Employees:</span>
                        <span className="font-medium">{calculation.employees}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Hours per Week:</span>
                        <span className="font-medium">{calculation.hoursPerWeek} hrs</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Daily Rate:</span>
                        <span className="font-medium">${calculation.dailyRate}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Annual Savings:</span>
                        <span className="font-medium text-emerald-600">${formatNumber(calculation.annualSavings)}</span>
                      </div>
                    </div>
                    
                    <div className="mt-4 flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="w-full text-xs" 
                        onClick={() => {
                          setEmployees([calculation.employees]);
                          setHoursPerWeek([calculation.hoursPerWeek]);
                          setdailyRate([calculation.dailyRate]);
                          setActiveTab("roi");
                          toast({
                            title: "Calculation Loaded",
                            description: "Previous calculation has been loaded.",
                          });
                        }}
                      >
                        Load
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="w-full text-xs text-red-600 hover:text-red-700 hover:bg-red-50" 
                        onClick={() => {
                          setSavedCalculations(savedCalculations.filter(calc => calc.id !== calculation.id));
                          toast({
                            title: "Calculation Deleted",
                            description: "Saved calculation has been removed.",
                          });
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ROICalculator;
